import { ChevronUp, Maximize2, MessageSquare, Minimize2, Minus, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { FollowUpChat } from "@/components/agents/FollowUpChat";

export type ChatMode = "closed" | "minimized" | "floating" | "fullscreen";

interface FloatingChatProps {
  mode: ChatMode;
  setMode: (next: ChatMode) => void;
  projectId: string;
  agentId: string;
  /** Nome do agente exibido no chat (ex.: "Architect"). O componente
   * nasceu no Functional; sem isso o título ficava hardcoded "Functional".
   * É nome próprio — quem chama decide se traduz (ex.: ProjectDiagnostic já
   * passa `t("Diagnostic")`); este componente só interpola o valor recebido. */
  agentLabel?: string;
  executionId?: string;
  isComplete: boolean;
  msgCount?: number;
}

export function FloatingChat({
  mode,
  setMode,
  projectId,
  agentId,
  agentLabel = "the agent",
  executionId,
  isComplete,
  msgCount = 0,
}: FloatingChatProps) {
  const { t } = useTranslation();
  if (mode === "closed") {
    return (
      <button
        type="button"
        onClick={() => setMode("floating")}
        title={t("Open chat")}
        aria-label={t("Open chat with {{agent}}", { agent: agentLabel })}
        className="fixed right-[18px] bottom-[44px] inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-[#08254c] border-0 cursor-pointer text-xs font-bold tracking-[0.04em] z-[40] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#08254c]"
        style={{
          background: "linear-gradient(135deg, #ffc83d, #ffb300)",
          boxShadow: "0 10px 30px rgba(8,37,76,0.25), 0 4px 12px rgba(255,200,61,0.4)",
        }}
      >
        <MessageSquare className="w-3.5 h-3.5" />
        <span>{t("Chat")}</span>
        {msgCount > 0 && (
          <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 bg-[#08254c] text-[#ffc83d] rounded-full font-mono text-[10px] font-bold">
            {msgCount}
          </span>
        )}
      </button>
    );
  }

  if (mode === "minimized") {
    return (
      <button
        type="button"
        onClick={() => setMode("floating")}
        aria-label={t("Restore chat panel")}
        className="fixed right-[18px] bottom-[44px] flex items-center gap-2 w-[280px] px-3.5 py-2.5 bg-[#08254c] text-white rounded-lg cursor-pointer text-[11.5px] font-semibold z-[40] border border-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#ffc83d]"
        style={{ boxShadow: "0 10px 30px rgba(8,37,76,0.25)" }}
      >
        <MessageSquare className="w-3 h-3" />
        {t("Chat with {{agent}}", { agent: agentLabel })}
        {msgCount > 0 && (
          <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 bg-[#ffc83d] text-[#08254c] rounded-full font-mono text-[10px] font-bold">
            {msgCount}
          </span>
        )}
        <ChevronUp className="w-3 h-3 ml-auto opacity-60" />
      </button>
    );
  }

  const fullscreen = mode === "fullscreen";

  return (
    <>
      {/* Expandido: backdrop que escurece o resto (vira modal focado em vez de
          uma coluna flutuante com a página distraindo dos lados). Clicar fora
          volta pro modo flutuante. */}
      {fullscreen && (
        <div
          className="fixed inset-0 bg-[#08254c]/35 backdrop-blur-[1px] z-[8]"
          onClick={() => setMode("floating")}
          aria-hidden="true"
        />
      )}
      <div
        role="dialog"
        aria-label={t("Chat with {{agent}}", { agent: agentLabel })}
        className={cn(
          "bg-white border border-[#e8ebf0] rounded-xl flex flex-col overflow-hidden z-[40] transition-[inset,width,height] duration-200 ease-out",
          fullscreen
          // Expandido = coluna CENTRALIZADA de largura confortável (não
          // edge-to-edge, que esticava as linhas e quebrava o layout). fixed =
          // viewport (~100vh): altura cheia, centrado no eixo X, ~880px (cap em
          // 92vw). flex-col + flex-1 nas mensagens preenche; input logo abaixo.
          // top-[56px] começa ABAIXO do ShellBar (h-12=48px, z-50) pra o header
          // do produto não cobrir o topo do chat. bottom-3.5 deixa margem.
          ? "fixed top-[56px] bottom-3.5 left-1/2 -translate-x-1/2 w-[min(880px,92vw)]"
          : "absolute right-[18px] bottom-[44px] w-[420px] max-w-[calc(100vw-36px)] h-[460px]",
      )}
      style={{
        boxShadow: "0 20px 60px rgba(8,37,76,0.22), 0 4px 12px rgba(8,37,76,0.08)",
      }}
    >
      <div className="px-3.5 py-2.5 bg-[#08254c] text-white flex justify-between items-center flex-shrink-0 border-b border-white/10">
        <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.14em] uppercase font-bold">
          <MessageSquare className="w-3 h-3" />
          <span>{t("Chat with {{agent}}", { agent: agentLabel })}</span>
          {msgCount > 0 && (
            <span className="text-white/45 font-medium tracking-[0.04em] normal-case">
              · {t("{{count}} msgs", { count: msgCount })}
            </span>
          )}
        </div>
        <div className="flex gap-1">
          <ChatHeaderButton onClick={() => setMode("minimized")} title={t("Minimize")} aria-label={t("Minimize")}>
            <Minus className="w-2.5 h-2.5" />
          </ChatHeaderButton>
          <ChatHeaderButton
            onClick={() => setMode(fullscreen ? "floating" : "fullscreen")}
            title={fullscreen ? t("Restore") : t("Open fullscreen")}
            aria-label={fullscreen ? t("Restore chat panel") : t("Open chat fullscreen")}
          >
            {fullscreen ? <Minimize2 className="w-2.5 h-2.5" /> : <Maximize2 className="w-2.5 h-2.5" />}
          </ChatHeaderButton>
          <ChatHeaderButton onClick={() => setMode("closed")} title={t("Close")} aria-label={t("Close chat")}>
            <X className="w-2.5 h-2.5" />
          </ChatHeaderButton>
        </div>
      </div>
      <div className="flex-1 min-h-0 overflow-auto bg-[#fafbfc] p-2.5 sku-floating-chat">
        {isComplete ? (
          <FollowUpChat
            projectId={projectId}
            agentId={agentId}
            executionId={executionId}
            isComplete={isComplete}
            fill
          />
        ) : (
          <div className="h-full flex items-center justify-center text-[#7b8799] text-xs text-center px-6">
            {t("The chat becomes available after a {{agent}} run completes.", { agent: agentLabel })}
          </div>
        )}
      </div>
      </div>
    </>
  );
}

function ChatHeaderButton({
  className,
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      className={cn(
        "w-6 h-[22px] inline-flex items-center justify-center bg-white/[0.06] border border-white/10 text-white/70 rounded cursor-pointer hover:bg-white/15 hover:text-white transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#ffc83d]",
        className,
      )}
      {...rest}
    />
  );
}
